/**
 * Turning end-user auth on, and the one input it takes.
 *
 * Extracted from the page for the same reason domain-state is: the
 * decisions are worth testing and the React around them is not.
 *
 * WHAT MAKES trustedOrigins DIFFERENT FROM EVERY OTHER TEXT FIELD HERE
 *
 * It is a CSRF control. Every origin in the list is a site that may drive
 * a signed-in user's session, so a permissive list is not a convenience -
 * it is somebody else's login form posting to yours. The API takes an
 * array of URLs and validates them; what this file does is stop the
 * obvious mistakes before a round trip, and make the empty case say what
 * it means rather than looking like a field somebody forgot.
 */

/** What a person typed, as the API wants it. */
export function parseOrigins(input: string): string[] {
  return input
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s !== '')
}

export type OriginProblem = { value: string; why: string }

/**
 * Origins that will be refused, and why, before anything is sent.
 *
 * The API validates too - this is not the boundary - but a 400 carrying a
 * zod path is a worse way to learn that you typed a path on the end of a
 * URL than a sentence next to the box you typed it in.
 */
export function checkOrigins(values: string[]): OriginProblem[] {
  const problems: OriginProblem[] = []
  for (const value of values) {
    let url: URL
    try {
      url = new URL(value)
    } catch {
      problems.push({ value, why: 'not a URL. Include the scheme, like https://app.example.com' })
      continue
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      problems.push({ value, why: 'only http and https origins can be trusted' })
      continue
    }
    // An origin is scheme + host + port and nothing else. A pasted address
    // bar carries a path, and a "trusted origin" with a path silently
    // matches nothing - the worst kind of wrong, because it looks right.
    if (url.pathname !== '/' || url.search !== '' || url.hash !== '') {
      problems.push({
        value,
        why: `an origin has no path. Use ${url.origin}`,
      })
      continue
    }
    if (values.filter((v) => v === value).length > 1) {
      problems.push({ value, why: 'listed more than once' })
    }
  }
  // One problem per value, even when it was listed three times.
  return problems.filter(
    (p, i) => problems.findIndex((q) => q.value === p.value) === i,
  )
}

/**
 * What to say about an empty list.
 *
 * Not a validation error: none-trusted is the safe default and a perfectly
 * reasonable state for an app that only talks to its own backend. But it
 * is a state somebody reaches by leaving a box alone, so it has to explain
 * itself rather than sit there looking unfinished.
 */
export const NO_ORIGINS =
  'No additional browser origins are trusted, which is the safe default. '
  + 'Same-origin apps are allowed; a server or mobile app needs no browser origin. '
  + 'Adding an origin alone does not configure cross-origin cookies or CORS.'

/**
 * What the page can honestly say about the trusted origins it was given.
 *
 * Three answers, not two. `null` means the platform has never recorded a
 * list for this project - it was enabled before the list was stored, and
 * the real one is a binding on its auth Worker that nothing can read back.
 * Rendering that as "no origin is trusted" would be a confident lie to the
 * one person who most needs the truth: somebody whose sign-in is being
 * refused and who is trying to find out why.
 */
export type OriginsView =
  | { kind: 'unrecorded' }
  | { kind: 'none' }
  | { kind: 'list'; origins: string[] }

export function originsView(origins: string[] | null | undefined): OriginsView {
  if (origins == null) return { kind: 'unrecorded' }
  if (origins.length === 0) return { kind: 'none' }
  return { kind: 'list', origins }
}

/** What an unrecorded list means for the person reading it. */
export const ORIGINS_UNRECORDED =
  'This project was set up before the platform kept this list, so it can only be read from the '
  + 'running auth service. Save the origins once and they will show here.'
